import { A4_HEIGHT_PX, Printable, usePrintableContext } from "./PrintableRoot";
import { Box, Card, Flex, Image, useComputedColorScheme } from "@mantine/core";
import { createContext, useContext, useEffect, useState } from "react";

// The report logo is height-pinned so swapping the asset can't change how much
// vertical room the footer takes from the A4 sheet. The card gives its children
// flex-basis: 0%, which in a column would collapse the pin back to the image's
// intrinsic height, so the logo opts out of flexing.
export const REPORT_LOGO_HEIGHT = "5rem";
export const REPORT_LOGO_FLEX = "0 0 auto";

const BREAK_STYLES: Record<BreakRule, React.CSSProperties> = {
  auto: {},
  avoid: { breakInside: "avoid", breakAfter: "page", breakBefore: "avoid" },
};

const SectionContext = createContext<{ floatingLogo: boolean }>({ floatingLogo: false });

export const usePrintableSectionContext = () => useContext(SectionContext);

interface SectionProps {
  children: React.ReactNode;
  breakRule?: BreakRule;
  floatingLogo?: boolean;
  orgDetail?: React.ReactNode;
  /**
   * Sections of one result that must print together. A section-scoped print
   * keeps the clicked section alone unless it carries a group, in which case
   * the whole group prints — otherwise a test whose result spans several
   * sections silently prints only the one holding the print button.
   */
  printGroup?: string;
}

export default function Section({
  children,
  breakRule = "avoid",
  floatingLogo = false,
  orgDetail,
  printGroup,
}: SectionProps) {
  const [rootFound, setRootFound] = useState(false);

  const { prettier, wrapperRef } = usePrintableContext();
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";
  const breakStyle = BREAK_STYLES[breakRule];

  useEffect(() => {
    setRootFound(!!wrapperRef.current);
  }, [wrapperRef]);

  const outerBg = prettier ? (isDark ? "var(--mantine-color-dark-8)" : "#218BE6") : "transparent";
  const cardBg = prettier ? (isDark ? "var(--oculera-card-bg)" : "#EEF4FD") : "transparent";

  const element = (
    <Box
      className="printable-section"
      data-print-group={printGroup}
      pos="relative"
      bg={outerBg}
      p={prettier ? "xl" : 0}
      mih={A4_HEIGHT_PX}
      style={{ ...breakStyle }}
    >
      <Card
        p={prettier ? "xl" : 0}
        bg={cardBg}
        radius="md"
        mih={A4_HEIGHT_PX - (prettier ? 64 : 0)}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <SectionContext.Provider value={{ floatingLogo }}>{children}</SectionContext.Provider>

        {floatingLogo && prettier ? (
          <Flex justify="space-between" align="center" mt="xs">
            {orgDetail ?? <Box />}
            <Image
              src="/img/logo.png"
              alt="Oculera Logo"
              h={REPORT_LOGO_HEIGHT}
              w="auto"
              flex={REPORT_LOGO_FLEX}
              fit="contain"
            />
          </Flex>
        ) : (
          prettier && (
            <Image
              src="/img/logo.png"
              alt="Oculera Logo"
              h={REPORT_LOGO_HEIGHT}
              w="auto"
              flex={REPORT_LOGO_FLEX}
              fit="contain"
              mt="xs"
              style={{ alignSelf: "flex-end" }}
            />
          )
        )}
      </Card>
    </Box>
  );

  return rootFound ? element : <Printable>{element}</Printable>;
}
